import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import { Category } from "@insite/client-framework/Services/CategoryService";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import {
    getRelatedProductsCollectionV2,
} from "@insite/client-framework/Services/ProductServiceV2";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { ProductInventoryDto, ProductModel, ProductPriceDto, WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import productClickTracking from "@MasterBluePrint/Tracking/ProductClick/productClickTracking";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    usedProductTranslateText = "usedProductTranslateText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.usedProductTranslateText]: string;
    };
}

export interface OtherConditionsStyles {
    otherConditionsWrapper?: InjectableCss;
    otherConditionsHeader?: InjectableCss;
    otherConditionsListStyles?: InjectableCss;
    productDetailLink: LinkPresentationProps;
}

export const otherConditionsStyles: OtherConditionsStyles = {
    otherConditionsWrapper: {
        css: css`
            margin-top:9px;
            border-top:1px solid rgba(204, 204, 204, 1);
            padding:13px 3px;
            @media screen and (max-width: 991px) {
                padding:13px 3px 0;
                display:none;
            }
        `,
    },
    otherConditionsHeader: {
        css: css`
            display:inline-block;
            margin-bottom:3px;
            font-family:'Lato', sans-serif;
            font-size:.8125rem;
            color:rgb(34,34,34);
            font-weight:700;
            letter-spacing:.01625rem
        `,
    },
    otherConditionsListStyles: {
        css: css`
            > ul {
                margin:0;
                padding:0; 
            }
            > ul > li {
                margin-bottom:8px;
                text-align:center; 
            }
            > ul > li a {
                display: flex;
                padding: 8px 16px;
                justify-content: center;
                align-items: center;
                gap: 10px;
                align-self: stretch;
                display:block;
                width:100%;
                margin:0;
                border:1px solid #001d42;
                border-radius:2px;
                background:#FFF;
                > span span {
                    color:#001d42 !important;
                    text-align: center;
                    font-family: Roboto;
                    font-size: 15px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 20px;
                    text-transform: uppercase;
                }
                > span span strong {font-weight:500 !important;}
                :hover,:active {
                    background: #005cb9;
                    border:1px solid #005cb9;
                    > span span {
                        color:#FFF !important;
                    }
                }
            }
        `,
    },
    productDetailLink: {
        css: css`
        span {
            font-family: Lato;
            font-size: 13px;
            font-style: normal;
            font-weight: 400;
            color: #333F48;
            line-height: normal;
        }
        `,
    },
}

const styles = otherConditionsStyles;

class OtherConditionProductsService {
    static instance: OtherConditionProductsService | null = null;
    private dataCache: Record<string, ProductModel[]> = {};
    private onGoingRequests: Record<string, Promise<ProductModel[]> | undefined> = {};

    constructor() {
        if (!OtherConditionProductsService.instance) {
            OtherConditionProductsService.instance = this;
        }
        return OtherConditionProductsService.instance;
    }

    static getInstance() {
        if (!OtherConditionProductsService.instance) {
            OtherConditionProductsService.instance = new OtherConditionProductsService();
        }
        return OtherConditionProductsService.instance;
    }

    fetchOtherConditionProducts(productId: string): Promise<ProductModel[]> {
        if (this.dataCache[productId]) {
            return Promise.resolve(this.dataCache[productId]);
        }
        if (this.onGoingRequests[productId]) {
            return this.onGoingRequests[productId];
        }
        const request = this.loadProducts(productId);
        this.onGoingRequests[productId] = request;
        return request;
    }

    private async loadProducts(productId: string): Promise<ProductModel[]> {
        try {
            const { products } = await getRelatedProductsCollectionV2({
                productId,
                relationship: "OtherConditions",
                pageSize: 999,
            });
            const result = products ?? [];
            this.dataCache[productId] = result;
            return result;
        } catch (error) {
            logger.error("Error loading other condition products:", error);
            throw error;
        } finally {
            delete this.onGoingRequests[productId];
        }
    }
}

const useOtherConditionProducts = (productId: string, isFeatureFlag?: boolean) => {
    const [products, setProducts] = useState<ProductModel[] | null>(null);

    useEffect(() => {
        if(!isFeatureFlag) {
            return;
        }
        let isMounted = true;
        const service = OtherConditionProductsService.getInstance();
        const fetch = async () => {
            try {
                const result = await service.fetchOtherConditionProducts(productId);
                if (isMounted) {
                    setProducts(result);
                }
            } catch (e) {
                logger.error("Failed to fetch other condition products", e);
            }
        };
        fetch();
        return () => {
            isMounted = false;
        };
    }, [productId, isFeatureFlag]);

    return products;
};

interface RelatedProduct {
    product: ProductModel;
    productInfo: ProductInfo;
}

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const currentPageUrl = location.pathname;
    const path = location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname;
    const catalogPage = getCatalogPageStateByPath(state, path).value;
    const category = getCategoryState(state, catalogPage?.categoryIdWithBrandId ?? catalogPage?.categoryId).value;
    
    return {
        currencySymbol: state.context.session.currency?.currencySymbol ?? "",
        currentPageUrl,
        category,
        productInfosById: state.pages.productDetails.productInfosById,
    };
};

const mapDispatchToProps = {
    loadRealTimePricing,
};

const ProductDetailsOtherConditionProducts: React.FC<Props> = ({
    product: { id },
    currencySymbol,
    loadRealTimePricing,
    currentPageUrl,
    category,
    productInfosById,
    fields,
    ...props
}) => {
    const dispatch = useAppDispatch();
    const [relatedProductList, setRelatedProductList] = useState<RelatedProduct[]>([]);
    const [inventoriesById, setInventoriesById] = useState<Record<string, ProductInventoryDto>>({});
    const featureFlags: string[] = ["TEUS-8334"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const isFeatureFlag = flagValues["TEUS-8334"];
    const otherProducts = useOtherConditionProducts(id, isFeatureFlag);

    useEffect(() => {
        if (!otherProducts || otherProducts.length === 0) {
            setRelatedProductList([]);
            return;
        }

        const relatedProductInfoList: RelatedProduct[] = otherProducts.map(product => ({
            product,
            productInfo: createFromProduct(product),
        }));

        loadRealTimePricing({
            productPriceParameters: relatedProductInfoList.map(p => p.productInfo),
            onSuccess: (pricingResults) => {
                pricingResults?.realTimePricingResults?.forEach((pricing: ProductPriceDto) => {
                    relatedProductInfoList.forEach(r => {
                        if (r.productInfo.productId === pricing.productId) {
                            r.productInfo.pricing = pricing;
                        }
                    });
                });
                setRelatedProductList(relatedProductInfoList);

                relatedProductInfoList.forEach(({ productInfo }) => {
                    dispatch(getRealTimeWarehouseInventory({
                        productId: productInfo.productId,
                        unitOfMeasure: productInfo.unitOfMeasure ?? "",
                        expand: ["warehouses"],
                        onComplete: result => {
                            const inv = (result as typeof result & { inventory?: ProductInventoryDto }).inventory;
                            if (inv) {
                                setInventoriesById(prev => ({ ...prev, [productInfo.productId]: inv }));
                            }
                        },
                    }));
                });
            }
        });
    }, [otherProducts]);

    if(!isFeatureFlag || relatedProductList?.length === 0) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.otherConditionsWrapper} className="otherConditionsList">
                <Typography {...styles.otherConditionsHeader}>Other Buying Options:</Typography>
                <StyledWrapper {...styles.otherConditionsListStyles}>
                    <ul>
                        {relatedProductList.map(product => (
                            <RelatedProductPriceDisplay
                                key={product.product.id}
                                currencySymbol={currencySymbol}
                                product={product.product}
                                productInfo={product.productInfo}
                                currentPageUrl={currentPageUrl}
                                category={category}
                                productInfosById={productInfosById}
                                inventoriesById={inventoriesById}
                                state={props}
                                parentProductId={id}
                                fields={fields}
                            />
                        ))}
                    </ul>
                </StyledWrapper>
            </StyledWrapper>            
        </>
    );
};

interface PriceProps extends RelatedProduct {
    currencySymbol: string;
    currentPageUrl: string;
    category: Category | undefined;
    productInfosById: any;
    inventoriesById: Record<string, ProductInventoryDto>;
    state: any;
    parentProductId: any;
    fields: {
        [fields.usedProductTranslateText]: string;
    };
}

const RelatedProductPriceDisplay: React.FC<PriceProps> = ({
    product,
    productInfo,
    currencySymbol,
    currentPageUrl,
    category,
    productInfosById,
    inventoriesById,
    state,
    fields,
    parentProductId
}) => {
    const price = productInfo.pricing
                    ? getUnitNetPrice(productInfo.pricing, productInfo.qtyOrdered || 1).price !== 0
                        ? getUnitNetPrice(productInfo.pricing, productInfo.qtyOrdered || 1).priceDisplay
                        : siteMessage("Price_Zero_Dollar_Label")
                    : currencySymbol;
    const productDetailLink = `/product/${product.urlSegment}`;
    const productCondition = product.properties?.condition?.toLowerCase() === 'used'
                            ?
                            fields.usedProductTranslateText :
                            product.properties?.condition;

    const handleProductClick = (e: React.MouseEvent) => {
        e.preventDefault();
        const childInventory = inventoriesById[product.id];
        const productContextModel = {
            product,
            productInfo,
            idx: 0,
        };
        const warehouses = childInventory?.inventoryWarehousesDtos?.[0]?.warehouseDtos as WarehouseDto[] | undefined;
        productClickTracking(
            productContextModel,
            "",
            "",
            category,
            currentPageUrl,
            state,
            warehouses,
            childInventory,
            "PDP Condition Button",
            parentProductId
        );
        window.location.href = productDetailLink;
    };
    
    return (
        <>
            <li key={product.id}>
            <Link {...styles.productDetailLink} href={productDetailLink}  onClick={handleProductClick}>
                    <span>
                        {productCondition}
                        &#32;-&#32;
                        <strong>{price}</strong>
                    </span>
                </Link>
            </li>
        </>
    )
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsOtherConditionProducts)),
    definition: {
        displayName: "Other Condition Products",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.usedProductTranslateText,
                displayName: "Used Product Text Translate",
                tooltip: "Enter translate text for used",
                editorTemplate: "TextField",
                defaultValue: "Certified Pre-Owned",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
